import React, { useState, useEffect, useCallback } from 'react';
import './HintBanner.css';

/**
 * Contextual hint system — shows dismissible tips based on milestones.
 * Each hint shows once, tracked via localStorage.
 *
 * Props:
 *   character  — character object from gameState
 *   inCombat   — boolean, currently in combat
 *   hasQuest   — boolean, character has active quests
 *   hasLevelUp — boolean, character has unspent skill points / pending level
 */

const HINTS = [
  {
    id: 'hint_first_combat',
    trigger: (props) => props.inCombat && (props.character?.level || 1) === 1,
    text: '⚔️ Tap a card to select • tap again or "Play" to cast • End Turn when AP runs out.',
    delay: 800,
  },
  {
    id: 'hint_class_l1',
    trigger: (props) => !props.inCombat && props.character?.level === 1,
    text: (props) => {
      const cls = props.character?.class;
      if (cls === 'Fighter') return '💪 You are a Fighter — strong in melee. Use Boost in combat to amplify your next attack.';
      if (cls === 'Mage') return '✨ You are a Mage — channel arcane seed spells. Try Magic Bolt or Fireball in combat!';
      if (cls === 'Cleric') return '🙏 You are a Cleric — your deity grants healing and miracles. Press T to see your prayers.';
      if (cls === 'Thief') return '🗡️ You are a Thief — strike from the shadows. You can Steal from enemies during combat!';
      if (cls === 'Psion') return '🧠 You are a Psion — bend reality with mental powers. Press T to see your starting disciplines.';
      if (cls === 'Druid') return '🌿 You are a Druid — wield nature\'s power. You can charm beasts and summon allies!';
      return '⚔️ Welcome, adventurer! Press T to view your starting abilities.';
    },
    delay: 1500,
  },
  {
    id: 'hint_movement',
    trigger: (props) => !props.inCombat && props.character?.level <= 2,
    text: '🧭 Use WASD or arrow keys to move. Walk into NPCs to talk!',
    delay: 3000,
  },
  {
    id: 'hint_quest_log',
    trigger: (props) => props.hasQuest && !props.inCombat,
    text: '📜 You have active quests! Press J to open your Quest Log.',
    delay: 2000,
  },
  {
    id: 'hint_potions',
    trigger: (props) => !props.inCombat && props.character?.level >= 2,
    text: '🧪 You start with health potions! Use them in combat by playing potion cards from your hand.',
    delay: 4000,
  },
  {
    id: 'hint_inventory',
    trigger: (props) => !props.inCombat && props.character?.level >= 2,
    text: '🎒 Press I to open your Inventory — manage potions and gear!',
    delay: 7000,
  },
  {
    id: 'hint_class_ability',
    trigger: (props) => !props.inCombat && props.character?.level >= 3,
    text: (props) => {
      const cls = props.character?.class;
      if (cls === 'Fighter') return '💪 As a Fighter, you have Boost — it increases your attack damage for a turn!';
      if (cls === 'Mage') return '✨ As a Mage, your seed spells grow stronger as you invest. Open your Spellbook to find higher-tier spells to learn!';
      if (cls === 'Cleric') return '🙏 As a Cleric, your deity grants divine prayers. Open your Spellbook for new healing and support spells!';
      if (cls === 'Thief') return '🗡️ As a Thief, you can Steal from enemies in combat and deal sneak attack damage!';
      if (cls === 'Psion') return '🧠 As a Psion, your discipline shapes your psionic powers. Open your Spellbook for new abilities!';
      if (cls === 'Druid') return '🌿 As a Druid, you can charm animals and summon nature allies! Open your Spellbook for new spells!';
      return '⚔️ Check your Skill Tree (T) to see your class abilities!';
    },
    delay: 4000,
  },
  {
    id: 'hint_levelup',
    trigger: (props) => props.hasLevelUp,
    text: '⬆️ You leveled up! Press T to open your Skill Tree and spend points.',
    delay: 1000,
  },
  {
    id: 'hint_deck',
    trigger: (props) => !props.inCombat && props.character?.level >= 2,
    text: '🃏 Customize your combat cards in the Deck Manager (🃏 button in the nav bar).',
    delay: 5000,
  },
  {
    id: 'hint_seeds',
    trigger: (props) => !props.inCombat && props.character?.level >= 3 && (props.character?.class === 'Mage' || props.character?.class === 'Cleric' || props.character?.class === 'Druid' || props.character?.class === 'Psion'),
    text: '🌿 Seeds unlock new spells as you gain mastery! Open your Spellbook to see the next tier — use spells in combat to earn Seed XP.',
    delay: 6000,
  },
  {
    id: 'hint_elemental',
    trigger: (props) => !props.inCombat && props.character?.level >= 3,
    text: '🔥❄️ Monsters resist or fear different elements — Fire melts Plants, Ice burns Fire creatures, Water snuffs Fire. Inspect a foe to see their weaknesses!',
    delay: 6500,
  },
  {
    id: 'hint_bounties',
    trigger: (props) => !props.inCombat && props.character?.level >= 4,
    text: '📋 Visit a Tavern to pick up Bounty contracts! Kill specific monsters for gold and XP rewards.',
    guideSection: 'challenge-loops',
    delay: 7000,
  },
  {
    id: 'hint_crafting',
    trigger: (props) => !props.inCombat && props.character?.level >= 5,
    text: '🔨 Found a crafting station? Use monster parts and gems to forge powerful gear!',
    guideSection: 'endgame-loot',
    delay: 8000,
  },
  {
    // v643.3 (#16): point new players at the Salvage option once they have
    // gear they don't need. Triggered around the level where extra drops
    // start piling up.
    id: 'hint_salvage',
    trigger: (props) => !props.inCombat && (props.character?.level || 0) >= 4,
    text: '♻️ Tip: open your Inventory (I) and right-click (or long-press) any unwanted item to Salvage it for crafting materials.',
    guideSection: 'endgame-loot',
    delay: 9000,
  },
  {
    id: 'hint_combos',
    trigger: (props) => !props.inCombat && props.character?.level >= 4,
    text: '⚡ Combine card types for combos! Playing Attack + Spell in one turn gives bonus effects.',
    delay: 6000,
  },
  {
    id: 'hint_skill_tree',
    trigger: (props) => !props.inCombat && props.character?.level >= 5,
    text: '🌳 Press T to invest skill points in your Skill Tree — unlock passive bonuses and new abilities!',
    delay: 5000,
  },
  {
    id: 'hint_heritage',
    trigger: (props) => !props.inCombat && props.character?.level >= 10,
    text: '🪶 Your Heritage node is unlocked! Press T — every race has a unique passive on the Heritage branch (always available, even after specializing).',
    delay: 4000,
  },
  {
    id: 'hint_explore',
    trigger: (props) => !props.inCombat && props.character?.level >= 8,
    text: '🗺️ Explore new areas by following paths at map edges. Each region has unique monsters and loot!',
    guideSection: 'atlas',
    delay: 7000,
  },
  // v643.2: progression milestone toasts — fire once at L10/L30/L50, link to guide
  {
    id: 'hint_milestone_l10',
    trigger: (props) => !props.inCombat && (props.character?.level || 0) >= 10,
    text: '🎯 Level 10 reached! New systems unlock: Heritage passives, advanced classes, and Bounty contracts. Tap Guide for what to focus on next.',
    guideSection: 'progression',
    delay: 3000,
  },
  {
    id: 'hint_milestone_l30',
    trigger: (props) => !props.inCombat && (props.character?.level || 0) >= 30,
    text: '🏆 Level 30 reached! Endgame systems open up — World Tier, advanced crafting, and elite bounties. Tap Guide for the mid-game roadmap.',
    guideSection: 'world-tiers',
    delay: 3000,
  },
  {
    id: 'hint_milestone_l50',
    trigger: (props) => !props.inCombat && (props.character?.level || 0) >= 50,
    text: '💎 Level 50 reached! You\'ve entered late-game. Push World Tier for richer rewards and chase legendary set pieces. Tap Guide for endgame loops.',
    guideSection: 'endgame-loot',
    delay: 3000,
  },
];

const HintBanner = ({ character, inCombat, hasQuest, hasLevelUp, onOpenGuide }) => {
  const [activeHint, setActiveHint] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const props = { character, inCombat, hasQuest, hasLevelUp };

  const charKey = (id) => `${id}_${character?.id || 'default'}`;

  const checkHints = useCallback(() => {
    if (dismissed) return;
    for (const hint of HINTS) {
      const seen = localStorage.getItem(charKey(hint.id));
      if (seen) continue;
      if (hint.trigger(props)) {
        setActiveHint(hint);
        return;
      }
    }
    setActiveHint(null);
  }, [character?.level, character?.id, inCombat, hasQuest, hasLevelUp, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const delay = activeHint ? 0 : (HINTS.find(h => !localStorage.getItem(charKey(h.id)))?.delay || 2000);
    const timer = setTimeout(checkHints, delay);
    return () => clearTimeout(timer);
  }, [checkHints, activeHint]);

  const handleDismiss = () => {
    if (activeHint) {
      localStorage.setItem(charKey(activeHint.id), '1');
    }
    setDismissed(true);
    setActiveHint(null);
    // Allow next hint to appear after a delay
    setTimeout(() => setDismissed(false), 10000);
  };

  if (!activeHint) return null;

  const hintText = typeof activeHint.text === 'function' ? activeHint.text(props) : activeHint.text;

  return (
    <div className="hint-banner" onClick={handleDismiss}>
      <span className="hint-text">{hintText}</span>
      {activeHint.guideSection && onOpenGuide && (
        <button
          type="button"
          className="hint-guide-btn"
          onClick={(event) => {
            event.stopPropagation();
            onOpenGuide(activeHint.guideSection);
          }}
        >
          Guide
        </button>
      )}
      <span className="hint-dismiss">✕</span>
    </div>
  );
};

export default HintBanner;
